import Vue from "vue";
import * as Sentry from "@sentry/vue";

export type SentryCaptureContext = any;

interface PendingSentryError {
    error: Error;
    context?: SentryCaptureContext;
}

type NotificationPayload = Record<string, any>;

const pendingSentryErrors: PendingSentryError[] = [];
let globalSentryHandlersBound = false;
let sentryInitialized = false;
let captureEnabled = false;

export function setSentryCaptureEnabled(enabled: boolean): void {
    captureEnabled = enabled;
    if (!captureEnabled) {
        pendingSentryErrors.length = 0;
    }
}

function normalizeError(error: any): Error {
    if (error instanceof Error) {
        return error;
    }

    if (typeof error === "string") {
        return new Error(error);
    }

    try {
        const serialized =
            error && typeof error === "object" ? JSON.stringify(error) : String(error);
        return new Error(serialized);
    } catch {
        return new Error(String(error));
    }
}

export function captureAppError(
    error: any,
    context?: SentryCaptureContext
): void {
    if (!captureEnabled) {
        return;
    }

    if (!error) {
        return;
    }

    const normalizedError = normalizeError(error);
    const client = Sentry.getClient();

    if (!client || !sentryInitialized) {
        if (pendingSentryErrors.length >= 50) {
            pendingSentryErrors.shift();
        }

        pendingSentryErrors.push({ error: normalizedError, context });
        return;
    }

    Sentry.captureException(normalizedError, context);
}

export function flushPendingSentryErrors(): void {
    if (!pendingSentryErrors.length) {
        return;
    }

    if (!captureEnabled) {
        pendingSentryErrors.length = 0;
        return;
    }

    while (pendingSentryErrors.length) {
        const entry = pendingSentryErrors.shift();
        if (!entry) {
            continue;
        }

        Sentry.captureException(entry.error, entry.context);
    }
}

export function hasSentryInitialized(): boolean {
    return sentryInitialized;
}

export function markSentryInitialized(): void {
    sentryInitialized = true;
    flushPendingSentryErrors();
}

function resolveComponentName(instance: Vue | undefined): string | undefined {
    if (!instance) {
        return undefined;
    }

    const options = instance.$options as Record<string, any>;
    return options?.name || options?._componentTag || options?._parentVnode?.tag;
}

function shouldCaptureNotification(payload: NotificationPayload): boolean {
    if (!payload) {
        return false;
    }

    if (payload.skipSentry === true) {
        return false;
    }

    if (payload.capture === true) {
        return true;
    }

    const type = (payload.type || "").toLowerCase();
    return type === "error" || type === "danger";
}

function buildNotificationContext(
    payload: NotificationPayload,
    vm: Vue | undefined
): SentryCaptureContext {
    const tags = {
        origin: "notification",
        notifyType: payload.type || "info",
        ...(payload.tags || {})
    };

    const extra = {
        title: payload.title,
        message: payload.message,
        duration: payload.duration,
        component: resolveComponentName(vm),
        ...((payload.extra as Record<string, any>) || {})
    };

    const customContext = payload.sentryContext as SentryCaptureContext | undefined;
    if (customContext) {
        return {
            ...customContext,
            tags: { ...tags, ...(customContext.tags || {}) },
            extra: { ...extra, ...(customContext.extra || {}) }
        };
    }

    return { tags, extra };
}

function createErrorFromNotification(payload: NotificationPayload): Error {
    if (payload.error) {
        return normalizeError(payload.error);
    }

    const message =
        payload.message ||
        payload.title ||
        "Unhandled notification error";

    const error = new Error(
        typeof message === "string" ? message : JSON.stringify(message)
    );

    error.name = payload.errorName || "NotificationError";

    return error;
}

function sanitizeNotificationOptions(
    payload: NotificationPayload
): NotificationPayload {
    const sanitized = { ...payload };
    delete sanitized.sentryContext;
    delete sanitized.capture;
    delete sanitized.skipSentry;
    delete sanitized.errorName;
    delete sanitized.error;
    delete sanitized.extra;
    delete sanitized.tags;
    return sanitized;
}

function normalizeNotificationInput(input: any): NotificationPayload {
    if (typeof input === "string") {
        return { message: input };
    }

    return { ...(input || {}) };
}

function wrapNotification(VueInstance: typeof Vue): void {
    const originalNotify = VueInstance.prototype.$notify;

    if (typeof originalNotify !== "function") {
        return;
    }

    if ((originalNotify as any).__sentryWrapped === true) {
        return;
    }

    const wrapped = function thisNotify(this: Vue, input: any) {
        const normalized = normalizeNotificationInput(input);

        if (!normalized.type && typeof input === "string") {
            normalized.type = "info";
        }

        const optionsForElement = sanitizeNotificationOptions(normalized);
        const result = originalNotify.call(this, optionsForElement as any);

        if (shouldCaptureNotification(normalized)) {
            const error = createErrorFromNotification(normalized);
            const context = buildNotificationContext(normalized, this);
            captureAppError(error, context);
        }

        return result;
    } as typeof Vue.prototype.$notify;

    const shortcuts = ["success", "warning", "info", "error"];
    shortcuts.forEach(type => {
        const originalShortcut = (originalNotify as any)[type];
        if (typeof originalShortcut === "function") {
            (wrapped as any)[type] = function notifyShortcut(this: Vue, input: any) {
                const normalized = normalizeNotificationInput(input);
                normalized.type = normalized.type || type;
                return wrapped.call(this, normalized);
            };
        }
    });

    ["close", "closeAll"].forEach(method => {
        if (typeof (originalNotify as any)[method] === "function") {
            (wrapped as any)[method] = (originalNotify as any)[method].bind(originalNotify);
        }
    });

    (wrapped as any).__sentryWrapped = true;

    VueInstance.prototype.$notify = wrapped;
}

export interface NotifyErrorOptions extends NotificationPayload {
    sentryContext?: SentryCaptureContext;
}

function createNotifyError(VueInstance: typeof Vue): void {
    VueInstance.prototype.$notifyError = function notifyError(
        this: Vue,
        error: any,
        options?: NotifyErrorOptions | string
    ) {
        const normalized = normalizeNotificationInput(options);

        normalized.type = normalized.type || "error";
        normalized.skipSentry = true;
        normalized.error = error;

        const context =
            normalized.sentryContext ||
            buildNotificationContext(normalized, this);

        captureAppError(error, context);

        // Pass the normalized payload (with skipSentry) so the wrapped $notify
        // sees the flag and does not re-capture. The wrapper sanitizes before
        // forwarding to ElementUI, so the flag never leaks downstream.
        return this.$notify(normalized as any);
    };
}

export function installErrorReporter(VueInstance: typeof Vue): void {
    wrapNotification(VueInstance);
    createNotifyError(VueInstance);

    VueInstance.prototype.$captureError = function captureFromVue(
        this: Vue,
        error: any,
        context?: SentryCaptureContext
    ) {
        captureAppError(error, context);
    };
}

// Sentry's default `globalHandlersIntegration` (auto-enabled in @sentry/vue
// v8) already captures `window.error` and `unhandledrejection`. Registering
// our own listeners on top of that produced duplicate Sentry events, so this
// is kept as a no-op for now; the export is retained so callers do not need
// to change.
export function setupGlobalSentryHandlers(): void {
    if (globalSentryHandlersBound) {
        return;
    }
    globalSentryHandlersBound = true;
}

declare module "vue/types/vue" {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface Vue {
        $captureError: (error: any, context?: SentryCaptureContext) => void;
        $notifyError: (
            error: any,
            options?: NotifyErrorOptions | string
        ) => void;
    }
}
